/**
 * PayApplicationsTab
 * ------------------
 * AIA G702 / G703 applications for payment. The schedule of values is seeded
 * from the project's CSI divisions plus approved change orders, with retainage
 * math and a two-page PDF (G702 certificate + G703 continuation sheet).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, Download, Send, Check, X, BadgeDollarSign, Receipt, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";

const fmt$ = (n) =>
  `${Number(n || 0) < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  certified: "bg-blue-50 text-blue-700 ring-blue-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  paid: "bg-emerald-600/10 text-emerald-800 ring-emerald-400",
  rejected: "bg-red-50 text-red-700 ring-red-300",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function PayApplicationsTab({ projectId, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [apps, setApps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pay-applications?project_id=${projectId}`, { headers });
      setApps(r.data?.pay_applications || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load pay applications");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const startNew = async () => {
    try {
      const r = await axios.get(`${API}/pay-applications/schedule-of-values?project_id=${projectId}`, { headers });
      setEditingId(null);
      setDraft({
        application_number: r.data.next_application_number,
        period_from: "", period_to: today(), application_date: today(),
        retainage_pct: 10, materials_retainage_pct: 10,
        contractor: r.data.defaults.contractor, architect: r.data.defaults.architect,
        notes: "", lines: r.data.lines,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the schedule of values");
    }
  };

  const openEdit = (app) => {
    setEditingId(app.id);
    setDraft({
      application_number: app.application_number,
      period_from: app.period_from || "", period_to: app.period_to || "",
      application_date: app.application_date || today(),
      retainage_pct: app.retainage_pct, materials_retainage_pct: app.materials_retainage_pct,
      contractor: app.contractor, architect: app.architect, notes: app.notes || "",
      lines: (app.computed?.lines || app.lines || []).map((l) => ({ ...l })),
    });
  };

  const setLine = (i, key, v) => setDraft((d) => ({
    ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, [key]: v } : l)),
  }));

  const calc = useMemo(() => {
    const lines = draft?.lines || [];
    const work = lines.reduce((s, l) => s + Number(l.previous_completed || 0) + Number(l.this_period || 0), 0);
    const stored = lines.reduce((s, l) => s + Number(l.materials_stored || 0), 0);
    const scheduled = lines.reduce((s, l) => s + Number(l.scheduled_value || 0), 0);
    const retainage = work * Number(draft?.retainage_pct || 0) / 100
      + stored * Number(draft?.materials_retainage_pct || 0) / 100;
    const earned = work + stored - retainage;
    const prevCerts = editingId
      ? (apps.find((a) => a.id === editingId)?.computed?.previous_certificates || 0)
      : (apps.filter((a) => a.status !== "draft").slice(-1)[0]?.computed?.total_earned_less_retainage || 0);
    return { scheduled, work, stored, completed: work + stored, retainage, earned, prevCerts, due: earned - prevCerts };
  }, [draft, apps, editingId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        period_from: draft.period_from || null, period_to: draft.period_to || null,
        application_date: draft.application_date,
        retainage_pct: Number(draft.retainage_pct || 0),
        materials_retainage_pct: Number(draft.materials_retainage_pct || 0),
        contractor: draft.contractor, architect: draft.architect, notes: draft.notes,
        lines: draft.lines.map((l) => ({
          item_no: String(l.item_no || ""), description: l.description,
          scheduled_value: Number(l.scheduled_value || 0),
          previous_completed: Number(l.previous_completed || 0),
          this_period: Number(l.this_period || 0),
          materials_stored: Number(l.materials_stored || 0),
          is_change_order: !!l.is_change_order,
        })),
      };
      if (editingId) await axios.put(`${API}/pay-applications/${editingId}`, body, { headers });
      else await axios.post(`${API}/pay-applications`, { project_id: projectId, ...body }, { headers });
      toast.success(`Application ${draft.application_number} saved`, { description: `Payment due ${fmt$(calc.due)}` });
      setDraft(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the application");
    } finally { setSaving(false); }
  };

  const act = async (app, action, extra = {}) => {
    try {
      const r = await axios.post(`${API}/pay-applications/${app.id}/action`, { action, ...extra }, { headers });
      toast.success(`Application ${app.application_number} ${r.data.pay_application.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action}`);
    }
  };

  const remove = async (app) => {
    if (!window.confirm(`Delete application ${app.application_number}?`)) return;
    try {
      await axios.delete(`${API}/pay-applications/${app.id}`, { headers });
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openPdf = async (app) => {
    try {
      const r = await axios.get(`${API}/pay-applications/${app.id}/pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `G702-App-${app.application_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the PDF");
    }
  };

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading applications…</p>;
  }

  const lastPosted = apps.filter((a) => a.status !== "draft").slice(-1)[0]?.computed || {};

  return (
    <div className="space-y-5" data-testid="pay-apps-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Applications for Payment</h2>
          <p className="text-sm text-slate-500">
            AIA G702 certificate with a G703 continuation sheet. Approved change orders appear as their own lines.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={startNew}
          data-testid="new-pay-app-btn">
          <Plus className="w-4 h-4 mr-2" />New Application
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["Contract sum to date", fmt$(summary.revised_contract_value), "text-slate-900",
              `Original ${fmt$(summary.original_contract_value)} + COs ${fmt$(summary.approved_change_total)}`],
            ["Billed to date", fmt$(lastPosted.total_completed_stored), "text-blue-700",
              `${apps.length} application${apps.length === 1 ? "" : "s"}`],
            ["Remaining to bill",
              fmt$(Number(summary.revised_contract_value || 0) - Number(lastPosted.total_completed_stored || 0)),
              "text-amber-600", ""],
            ["Retainage held", fmt$(lastPosted.total_retainage), "text-emerald-600", "At current retainage rate"],
          ].map(([label, value, accent, sub]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
              <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
              {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
            </div>
          ))}
        </div>
      )}

      {apps.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white" data-testid="pay-apps-empty">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No payment applications yet</p>
          <p className="text-sm text-slate-400 mt-1">The schedule of values is built from your divisions and approved change orders.</p>
          <Button size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800 text-white" onClick={startNew}>
            Start Application No. 1
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["App", "Period", "Completed & stored", "% done", "Retainage", "Payment due", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apps.map((app) => {
                const c = app.computed || {};
                return (
                  <tr key={app.id} className="hover:bg-slate-50/70" data-testid={`pay-app-row-${app.application_number}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-blue-700">No. {app.application_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{app.period_from || "—"} → {app.period_to || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-800">{fmt$(c.total_completed_stored)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{c.percent_complete ?? 0}%</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmt$(c.total_retainage)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-900 whitespace-nowrap">{fmt$(c.current_payment_due)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[app.status]}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {["draft", "rejected"].includes(app.status) && (
                          <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                            onClick={() => act(app, "submit")} data-testid={`pay-app-submit-${app.application_number}`}>
                            <Send className="w-3.5 h-3.5 mr-1" />Submit
                          </Button>
                        )}
                        {app.status === "submitted" && (
                          <>
                            <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                              onClick={() => act(app, "certify")} data-testid={`pay-app-certify-${app.application_number}`}>
                              <Check className="w-3.5 h-3.5 mr-1" />Certify
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                              onClick={() => act(app, "reject", { note: window.prompt("Reason?") || "" })}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        {["submitted", "certified"].includes(app.status) && (
                          <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                            onClick={() => act(app, "approve")} data-testid={`pay-app-approve-${app.application_number}`}>
                            Approve
                          </Button>
                        )}
                        {["approved", "certified"].includes(app.status) && (
                          <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                            onClick={() => act(app, "mark_paid", { payment_reference: window.prompt("Payment reference / check no.?") || "" })}
                            data-testid={`pay-app-paid-${app.application_number}`}>
                            <BadgeDollarSign className="w-3.5 h-3.5 mr-1" />Mark paid
                          </Button>
                        )}
                        {!["approved", "paid"].includes(app.status) && (
                          <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                            onClick={() => openEdit(app)}>Edit</Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                          onClick={() => openPdf(app)} data-testid={`pay-app-pdf-${app.application_number}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {!["approved", "paid"].includes(app.status) && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                            onClick={() => remove(app)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application for Payment No. {draft?.application_number}</DialogTitle>
            <DialogDescription>
              Bill this period against the schedule of values. Retainage and previous certificates are applied automatically.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                {[
                  ["Period from", "period_from", "date"],
                  ["Period to", "period_to", "date"],
                  ["Application date", "application_date", "date"],
                  ["Retainage %", "retainage_pct", "number"],
                  ["Stored mat. retainage %", "materials_retainage_pct", "number"],
                  ["Contractor", "contractor", "text"],
                ].map(([label, key, type]) => (
                  <div key={key}>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</label>
                    <Input type={type} value={draft[key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                      data-testid={`pay-app-${key}`} />
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full min-w-[880px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Item", "Description of work", "Scheduled value", "From previous app.",
                        "This period", "Materials stored", "Total & %", "Balance"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {draft.lines.map((l, i) => {
                      const total = Number(l.previous_completed || 0) + Number(l.this_period || 0) + Number(l.materials_stored || 0);
                      const pct = Number(l.scheduled_value || 0) ? (total / Number(l.scheduled_value) * 100).toFixed(1) : 0;
                      return (
                        <tr key={i}>
                          <td className="px-3 py-2 text-xs text-slate-400">{l.item_no}</td>
                          <td className="px-3 py-2 text-sm text-slate-800">
                            {l.description}
                            {l.is_change_order ? <span className="ml-1 text-[10px] font-black text-emerald-600">CO</span> : null}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap">{fmt$(l.scheduled_value)}</td>
                          <td className="px-3 py-2 text-sm text-slate-500 whitespace-nowrap">{fmt$(l.previous_completed)}</td>
                          <td className="px-3 py-2">
                            <Input type="number" className="w-28 h-8" value={l.this_period ?? ""}
                              onChange={(e) => setLine(i, "this_period", e.target.value)}
                              data-testid={`pay-app-line-period-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <Input type="number" className="w-28 h-8" value={l.materials_stored ?? ""}
                              onChange={(e) => setLine(i, "materials_stored", e.target.value)} />
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-800 whitespace-nowrap">
                            {fmt$(total)} <span className="text-slate-400">· {pct}%</span>
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-500 whitespace-nowrap">
                            {fmt$(Number(l.scheduled_value || 0) - total)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Notes</label>
                  <Textarea rows={4} value={draft.notes}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm space-y-1.5"
                  data-testid="pay-app-g702-preview">
                  {[
                    ["Contract sum to date", summary?.revised_contract_value],
                    ["Total completed & stored", calc.completed],
                    ["Total retainage", -calc.retainage],
                    ["Total earned less retainage", calc.earned],
                    ["Less previous certificates", -calc.prevCerts],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-slate-500">
                      <span>{k}</span><b className="text-slate-800">{fmt$(v)}</b>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-300 pt-2 mt-1">
                    <span className="font-black uppercase tracking-wider text-[11px] text-slate-500 self-center">Current payment due</span>
                    <b className="text-xl text-emerald-600" data-testid="pay-app-due-preview">{fmt$(calc.due)}</b>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="pay-app-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingId ? "Save application" : "Create application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
